import { t } from "@lingui/macro";
import type { User } from "@repo/common";
import {
	auth,
	ConnectWhatsAppButton,
	disconnectWhatsApp,
	inviteUserToCompany,
	isGoogleLinked,
	linkGoogleAccount,
	refreshCompanyOutboundCallerIdVerification,
	removeCompanyOutboundCallerId,
	requestSubscriptionCancellation,
	sendCompanyUserPasswordReset,
	setCompanyUserInternalAuthPhrase,
	startCompanyOutboundCallerIdVerification,
} from "@repo/ui";
import {
	Briefcase,
	CheckCircle2,
	CreditCard,
	Edit,
	ExternalLink,
	Fingerprint,
	KeyRound,
	Languages,
	Loader2,
	MessageCircle,
	MoreHorizontal,
	PhoneOutgoing,
	RefreshCw,
	ShieldCheck,
	ShieldX,
	Trash2,
	UserPlus,
	Users,
} from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import { useData } from "../contexts/DataContext";
import { i18n } from "../i18n";
import { isAdminRole } from "../utils/auth";
import { formatPhoneNumber } from "../utils/formatPhoneNumber";
import { getContentFieldProps } from "../utils/textDirection";
import { CallResultsSettingsPanel } from "./CallResultsSettingsPanel";
import { CollapsibleSettingsPanel } from "./CollapsibleSettingsPanel";
import { CompanyWhatsAppTemplates } from "./CompanyWhatsAppTemplates";
import { DeveloperKeysSettingsCard } from "./DeveloperKeysSettingsCard";
import { DtmfSettingsCard } from "./DtmfSettingsCard";
import { ExternalAgentDeploymentApprovalsCard } from "./ExternalAgentDeploymentApprovalsCard";
import { ExternalOperationApprovalsCard } from "./ExternalOperationApprovalsCard";
import { GoogleCalendarSettingsCard } from "./GoogleCalendarSettingsCard";
import { GrokbotSettingsCard } from "./GrokbotSettingsCard";
import { PageHeader } from "./PageHeader";
import { SancarToolsPanel } from "./SancarToolsPanel";
import { SettingsNavigation, SettingsSection } from "./SettingsNavigation";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "./ui/alert-dialog";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "./ui/dialog";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "./ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "./ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

type CompanyUserRow = User & { uid?: string };

const FALLBACK_COMPANY_TIME_ZONES = [
	"Asia/Jerusalem",
	"UTC",
	"Europe/London",
	"Europe/Paris",
	"Europe/Berlin",
	"Asia/Dubai",
	"America/Toronto",
	"America/New_York",
	"America/Los_Angeles",
] as const;

const COMPANY_TIME_ZONE_OPTIONS = [
	...new Set([
		"Asia/Jerusalem",
		"UTC",
		...(typeof Intl.supportedValuesOf === "function"
			? Intl.supportedValuesOf("timeZone")
			: FALLBACK_COMPANY_TIME_ZONES),
	]),
];

export function SettingsPage() {
	const {
		user,
		language,
		setLanguage,
		companyProfile,
		agents,
		users,
		subscription,
		updateCompanyProfile,
		updateCompanyUser,
		deleteCompanyUser,
	} = useData();
	const contentFieldProps = (
		value: string | null | undefined,
		extraClassName = "",
	) =>
		getContentFieldProps(
			value,
			language === "he" ? "rtl" : "ltr",
			extraClassName,
		);

	const [companyName, setCompanyName] = useState("");
	const [address, setAddress] = useState("");
	const [contactName, setContactName] = useState("");
	const [contactEmail, setContactEmail] = useState("");
	const [contactPhone, setContactPhone] = useState("");
	const [companyTimeZone, setCompanyTimeZone] = useState("Asia/Jerusalem");
	const [isCompanyTimeZoneOpen, setIsCompanyTimeZoneOpen] = useState(false);
	const [isSavingCompany, setIsSavingCompany] = useState(false);
	const [outboundCallerIdInput, setOutboundCallerIdInput] = useState("");
	const [outboundCallerIdValidationCode, setOutboundCallerIdValidationCode] =
		useState<string | null>(null);
	const [outboundCallerIdAction, setOutboundCallerIdAction] = useState<
		"start" | "refresh" | "remove" | null
	>(null);
	const [showWhatsAppDisconnectDialog, setShowWhatsAppDisconnectDialog] =
		useState(false);
	const [isDisconnectingWhatsApp, setIsDisconnectingWhatsApp] = useState(false);

	const handleDisconnectWhatsApp = async () => {
		if (!companyProfile?.id) return;
		setIsDisconnectingWhatsApp(true);
		try {
			await disconnectWhatsApp({ companyId: companyProfile.id });
			toast.success(
				t({ id: "whatsappDisconnected", message: "WhatsApp disconnected" }),
			);
			setShowWhatsAppDisconnectDialog(false);
		} catch {
			toast.error(
				t({
					id: "whatsappDisconnectFailed",
					message: "Unable to disconnect WhatsApp. Please try again.",
				}),
			);
		} finally {
			setIsDisconnectingWhatsApp(false);
		}
	};

	const [editingUser, setEditingUser] = useState<CompanyUserRow | null>(null);
	const [editUserName, setEditUserName] = useState("");
	const [editUserPhone, setEditUserPhone] = useState("");
	const [editUserRole, setEditUserRole] = useState<"user" | "admin">("user");
	const [isSavingUser, setIsSavingUser] = useState(false);
	const [isLinkingGoogle, setIsLinkingGoogle] = useState(false);

	const handleLinkGoogle = async () => {
		if (!auth.currentUser) return;
		setIsLinkingGoogle(true);
		try {
			const res = await linkGoogleAccount(auth.currentUser);
			if (res) {
				toast.success(
					t({
						id: "googleLinkedSuccess",
						message: "Google account successfully linked!",
					}),
				);
			}
		} catch (error: unknown) {
			console.error("Failed to link Google account", error);
			toast.error(
				(error as { message?: string } | null)?.message ||
					t({
						id: "googleLinkFailed",
						message: "Failed to link Google account.",
					}),
			);
		} finally {
			setIsLinkingGoogle(false);
		}
	};
	const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);
	const [inviteName, setInviteName] = useState("");
	const [inviteEmail, setInviteEmail] = useState("");
	const [invitePhone, setInvitePhone] = useState("");
	const [inviteRole, setInviteRole] = useState<"user" | "admin">("user");
	const [isInvitingUser, setIsInvitingUser] = useState(false);
	const [passwordResetUser, setPasswordResetUser] =
		useState<CompanyUserRow | null>(null);
	const [isSendingPasswordReset, setIsSendingPasswordReset] = useState(false);
	const [phraseResetUser, setPhraseResetUser] = useState<CompanyUserRow | null>(
		null,
	);
	const [authPhrase, setAuthPhrase] = useState("");
	const [authPhraseConfirm, setAuthPhraseConfirm] = useState("");
	const [isSavingAuthPhrase, setIsSavingAuthPhrase] = useState(false);

	const isAdmin = isAdminRole(user?.role);

	const [showCancelDialog, setShowCancelDialog] = useState(false);
	const [isCanceling, setIsCanceling] = useState(false);

	const handleConfirmCancel = async () => {
		if (!subscription || !companyProfile?.id) return;
		setIsCanceling(true);
		try {
			const result = await requestSubscriptionCancellation({
				subscriptionId: subscription.id!,
				companyId: companyProfile.id,
			});
			if (result.data.success) {
				toast.success(
					t({
						id: "subCancelSuccess",
						message: "Subscription cancellation requested successfully.",
					}),
				);
			}
			setShowCancelDialog(false);
		} catch (error) {
			console.error("Failed to cancel subscription", error);
			toast.error(
				t({ id: "subCancelFailed", message: "Failed to cancel subscription." }),
			);
		} finally {
			setIsCanceling(false);
		}
	};
	const userRoleLabel = (role?: string) => {
		switch (role) {
			case "admin":
				return t({ id: "adminRole", message: "Admin" });
			case "system-admin":
				return t({ id: "systemAdminRole", message: "System Admin" });
			default:
				return t({ id: "userRole", message: "User" });
		}
	};

	// Sync local state with company profile
	React.useEffect(() => {
		if (companyProfile) {
			setCompanyName(companyProfile.name || "");
			setAddress(companyProfile.address || "");
			setContactName(companyProfile.contactName || "");
			setContactEmail(companyProfile.contactEmail || "");
			setContactPhone(companyProfile.contactPhone || "");
			setCompanyTimeZone(companyProfile.timeZone || "Asia/Jerusalem");
			setOutboundCallerIdInput(
				companyProfile.outboundCallerIds?.[0]?.phoneNumber || "",
			);
		}
	}, [companyProfile]);

	const configuredOutboundCallerId = companyProfile?.outboundCallerIds?.[0];

	const handleStartOutboundCallerIdVerification = async () => {
		if (!companyProfile?.id || !outboundCallerIdInput.trim()) return;
		setOutboundCallerIdAction("start");
		setOutboundCallerIdValidationCode(null);
		try {
			const result = await startCompanyOutboundCallerIdVerification({
				companyId: companyProfile.id,
				phoneNumber: outboundCallerIdInput.trim(),
			});
			setOutboundCallerIdValidationCode(result.data.validationCode ?? null);
			toast.success(
				result.data.callerId.status === "verified"
					? t({
							id: "outboundCallerIdAlreadyVerified",
							message: "This outbound number is already verified.",
						})
					: t({
							id: "outboundCallerIdVerificationStarted",
							message: "Verification call started.",
						}),
			);
		} catch (error) {
			console.error("Failed to start outbound caller ID verification", error);
			toast.error(
				t({
					id: "outboundCallerIdVerificationFailed",
					message: "Could not start phone number verification.",
				}),
			);
		} finally {
			setOutboundCallerIdAction(null);
		}
	};

	const handleRefreshOutboundCallerId = async () => {
		if (!companyProfile?.id) return;
		setOutboundCallerIdAction("refresh");
		try {
			const result = await refreshCompanyOutboundCallerIdVerification({
				companyId: companyProfile.id,
			});
			toast.success(
				result.data.callerId?.status === "verified"
					? t({
							id: "outboundCallerIdVerifiedSuccess",
							message: "Outbound number verified successfully.",
						})
					: t({
							id: "outboundCallerIdStillUnverified",
							message: "The outbound number is not verified yet.",
						}),
			);
		} catch (error) {
			console.error("Failed to refresh outbound caller ID verification", error);
			toast.error(
				t({
					id: "outboundCallerIdRefreshFailed",
					message: "Could not refresh verification status.",
				}),
			);
		} finally {
			setOutboundCallerIdAction(null);
		}
	};

	const handleRemoveOutboundCallerId = async () => {
		if (!companyProfile?.id) return;
		setOutboundCallerIdAction("remove");
		try {
			await removeCompanyOutboundCallerId({ companyId: companyProfile.id });
			setOutboundCallerIdInput("");
			setOutboundCallerIdValidationCode(null);
			toast.success(
				t({
					id: "outboundCallerIdRemoved",
					message: "Outbound number removed.",
				}),
			);
		} catch (error) {
			console.error("Failed to remove outbound caller ID", error);
			toast.error(
				t({
					id: "outboundCallerIdRemoveFailed",
					message: "Could not remove the outbound number.",
				}),
			);
		} finally {
			setOutboundCallerIdAction(null);
		}
	};

	const handleSaveCompany = async () => {
		setIsSavingCompany(true);
		try {
			await updateCompanyProfile({
				name: companyName,
				address,
				contactName,
				contactEmail,
				contactPhone,
				timeZone: companyTimeZone,
			});
		} catch (error) {
			console.error("Failed to save company profile:", error);
		} finally {
			setIsSavingCompany(false);
		}
	};

	const handleEditUser = (u: CompanyUserRow) => {
		setEditingUser(u);
		setEditUserName(u.displayName || "");
		setEditUserPhone(u.phoneNumber || "");
		setEditUserRole(u.role === "admin" ? "admin" : "user");
	};

	const handleSaveUser = async () => {
		if (!editingUser) return;
		setIsSavingUser(true);
		try {
			await updateCompanyUser((editingUser.uid || editingUser.id)!, {
				displayName: editUserName,
				phoneNumber: editUserPhone,
				role: editUserRole,
			});
			toast.success(
				t({
					id: "userUpdatedSuccess",
					message: "User updated successfully.",
				}),
			);
			setEditingUser(null);
		} catch (error) {
			console.error("Failed to update user:", error);
			toast.error(
				error instanceof Error
					? error.message
					: t({
							id: "userUpdateFailed",
							message: "Failed to update user.",
						}),
			);
		} finally {
			setIsSavingUser(false);
		}
	};

	const handleInviteUser = async () => {
		if (!companyProfile?.id || !inviteName.trim() || !inviteEmail.trim()) {
			toast.error(
				t({
					id: "inviteUserRequiredFields",
					message: "Enter the user's name and email address.",
				}),
			);
			return;
		}

		if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(inviteEmail.trim())) {
			toast.error(
				t({
					id: "inviteUserInvalidEmail",
					message: "Enter a valid email address.",
				}),
			);
			return;
		}

		setIsInvitingUser(true);
		try {
			const trimmedPhone = invitePhone.trim();
			const result = await inviteUserToCompany({
				companyId: companyProfile.id,
				email: inviteEmail.trim(),
				displayName: inviteName.trim(),
				...(trimmedPhone ? { phoneNumber: trimmedPhone } : {}),
				role: inviteRole,
			});
			if (result.data.status === "already-member") {
				toast.success(
					t({
						id: "inviteUserAlreadyMember",
						message: "This user is already a member of the company.",
					}),
				);
			} else {
				toast.success(
					t({
						id: "inviteUserSuccess",
						message: "Invitation email sent successfully.",
					}),
				);
			}
			setIsInviteDialogOpen(false);
			setInviteName("");
			setInviteEmail("");
			setInvitePhone("");
			setInviteRole("user");
		} catch (error) {
			console.error("Failed to invite user:", error);
			toast.error(
				error instanceof Error
					? error.message
					: t({
							id: "inviteUserFailed",
							message: "Failed to invite user.",
						}),
			);
		} finally {
			setIsInvitingUser(false);
		}
	};

	const handleSendPasswordReset = async () => {
		if (!companyProfile?.id || !passwordResetUser) return;

		setIsSendingPasswordReset(true);
		try {
			await sendCompanyUserPasswordReset({
				companyId: companyProfile.id,
				userId: (passwordResetUser.uid || passwordResetUser.id)!,
			});
			toast.success(
				t({
					id: "companyPasswordResetSuccess",
					message: "Password reset email sent successfully.",
				}),
			);
			setPasswordResetUser(null);
		} catch (error) {
			console.error("Failed to send password reset:", error);
			const errorCode =
				typeof error === "object" &&
				error !== null &&
				"code" in error &&
				typeof error.code === "string"
					? error.code
					: "";
			toast.error(
				errorCode.endsWith("resource-exhausted")
					? t({
							id: "companyPasswordResetCooldown",
							message:
								"Please wait before sending another password reset email to this user.",
						})
					: t({
							id: "companyPasswordResetFailed",
							message: "Failed to send password reset email.",
						}),
			);
		} finally {
			setIsSendingPasswordReset(false);
		}
	};

	const normalizeAuthPhrase = (value: string) =>
		value
			.normalize("NFKD")
			.toLocaleLowerCase()
			.replace(/[^\p{L}]/gu, "");

	const handleSaveAuthPhrase = async () => {
		if (!companyProfile?.id || !phraseResetUser) return;
		const normalizedPhrase = normalizeAuthPhrase(authPhrase);
		if (normalizedPhrase.length < 4) {
			toast.error(
				t({
					id: "internalAuthPhraseTooShort",
					message:
						"Enter an authentication phrase with at least 4 letters after normalization.",
				}),
			);
			return;
		}
		if (normalizedPhrase !== normalizeAuthPhrase(authPhraseConfirm)) {
			toast.error(
				t({
					id: "internalAuthPhraseMismatch",
					message: "Authentication phrases do not match.",
				}),
			);
			return;
		}

		setIsSavingAuthPhrase(true);
		try {
			await setCompanyUserInternalAuthPhrase({
				companyId: companyProfile.id,
				userId: (phraseResetUser.uid || phraseResetUser.id)!,
				phrase: normalizedPhrase,
			});
			toast.success(
				t({
					id: "internalAuthPhraseSaved",
					message: "Authentication phrase saved successfully.",
				}),
			);
			setPhraseResetUser(null);
			setAuthPhrase("");
			setAuthPhraseConfirm("");
		} catch (error) {
			console.error("Failed to save authentication phrase:", error);
			toast.error(
				t({
					id: "internalAuthPhraseSaveFailed",
					message: "Failed to save authentication phrase.",
				}),
			);
		} finally {
			setIsSavingAuthPhrase(false);
		}
	};

	const [userToDelete, setUserToDelete] = useState<CompanyUserRow | null>(null);
	const [isDeletingUser, setIsDeletingUser] = useState(false);

	const handleDeleteUser = async () => {
		if (!userToDelete) return;
		setIsDeletingUser(true);
		try {
			await deleteCompanyUser((userToDelete.uid || userToDelete.id)!);
			setUserToDelete(null);
		} catch (error) {
			console.error("Failed to delete user:", error);
		} finally {
			setIsDeletingUser(false);
		}
	};

	return (
		<div
			className="mx-auto max-w-7xl space-y-6 px-4 pb-10"
			data-testid="settings-page"
		>
			<PageHeader
				title={t({ id: "settings", message: "Settings" })}
				description={t({
					id: "settingsPageSubtitle",
					message: "Manage your company, call results, channels, and access.",
				})}
			/>
			<SettingsNavigation
				isAdmin={isAdmin}
				developerEnabled={
					!!companyProfile?.id && companyProfile.features?.apiAccess === true
				}
			>
				<SettingsSection id="whatsapp-templates">
					{companyProfile?.id && (
						<CompanyWhatsAppTemplates
							companyId={companyProfile.id}
							canManage={isAdmin}
						/>
					)}
				</SettingsSection>
				<SettingsSection id="company">
					{/* Company Profile Settings */}
					<CollapsibleSettingsPanel
						icon={<Briefcase className="w-5 h-5" />}
						title={t({ id: "companyProfile", message: "Company Profile" })}
						description={t({
							id: "companyProfileSubtitle",
							message: "Update your company identity and contact information",
						})}
					>
						<div className="space-y-4">
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label htmlFor="companyName">
										{t({ id: "companyName", message: "Company Name" })}
									</Label>
									<Input
										id="companyName"
										value={companyName}
										onChange={(e) => setCompanyName(e.target.value)}
										{...contentFieldProps(companyName)}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="address">
										{t({ id: "address", message: "Address" })}
									</Label>
									<Input
										id="address"
										value={address}
										onChange={(e) => setAddress(e.target.value)}
										{...contentFieldProps(address)}
									/>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="contactName"
										className={language === "he" ? "text-right block" : ""}
									>
										{t({
											id: "primaryContactName",
											message: "Primary Contact Name",
										})}
									</Label>
									<Input
										id="contactName"
										value={contactName}
										onChange={(e) => setContactName(e.target.value)}
										{...contentFieldProps(contactName)}
									/>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="contactEmail"
										className={language === "he" ? "text-right block" : ""}
									>
										{t({
											id: "primaryContactEmail",
											message: "Primary Contact Email",
										})}
									</Label>
									<Input
										id="contactEmail"
										type="email"
										value={contactEmail}
										onChange={(e) => setContactEmail(e.target.value)}
										dir="ltr"
										className="text-left"
									/>
								</div>
								<div className="space-y-2">
									<Label
										htmlFor="contactPhone"
										className={language === "he" ? "text-right block" : ""}
									>
										{t({
											id: "primaryContactPhone",
											message: "Primary Contact Phone",
										})}
									</Label>
									<Input
										id="contactPhone"
										value={contactPhone}
										onChange={(e) => setContactPhone(e.target.value)}
										dir="ltr"
										className="text-left"
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="companyTimeZone">
										{t({
											id: "companyDefaultTimeZone",
											message: "Default time zone",
										})}
									</Label>
									<Select
										value={companyTimeZone}
										onValueChange={setCompanyTimeZone}
										onOpenChange={setIsCompanyTimeZoneOpen}
									>
										<SelectTrigger id="companyTimeZone" dir="ltr">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{(isCompanyTimeZoneOpen
												? COMPANY_TIME_ZONE_OPTIONS
												: [companyTimeZone]
											).map((timeZone) => (
												<SelectItem key={timeZone} value={timeZone}>
													{timeZone}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<p className="text-sm text-muted-foreground">
										{t({
											id: "companyDefaultTimeZoneHelp",
											message:
												"Used to group company-wide activity into calendar days.",
										})}
									</p>
								</div>
							</div>
							<Button onClick={handleSaveCompany} disabled={isSavingCompany}>
								{isSavingCompany
									? t({ id: "saving", message: "Saving..." })
									: t({ id: "saveChanges", message: "Save Changes" })}
							</Button>
						</div>
					</CollapsibleSettingsPanel>
					{/* Subscription settings card */}
					{subscription && (
						<CollapsibleSettingsPanel
							icon={<CreditCard className="w-5 h-5" />}
							title={t({
								id: "subscriptionPlan",
								message: "Subscription Plan",
							})}
							description={t({
								id: "subscriptionSubtitle",
								message: "View details and manage your subscription",
							})}
							headerAction={
								<Badge variant="secondary">{subscription.planName}</Badge>
							}
						>
							<div className="space-y-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div className="space-y-1">
										<Label className="text-muted-foreground">
											{t({ id: "currentPlanName", message: "Current Plan" })}
										</Label>
										<p className="text-lg font-semibold text-slate-900">
											{subscription.planName}
										</p>
									</div>
									<div className="space-y-1">
										<Label className="text-muted-foreground">
											{t({
												id: "nextBillingDate",
												message: "Next Billing Date",
											})}
										</Label>
										<p className="text-lg font-medium text-slate-800">
											{subscription.nextBillingDate
												? new Date(
														subscription.nextBillingDate,
													).toLocaleDateString(navigator.language)
												: "—"}
										</p>
									</div>
								</div>

								{subscription.cancelAtPeriodEnd ? (
									<div className="p-3 bg-warn-tint border border-warn/30 text-foreground rounded-lg text-sm font-medium">
										{t({
											id: "subscriptionCancelWarning",
											message:
												"Your subscription is scheduled for cancellation and will remain active until the end of the current cycle.",
										})}
									</div>
								) : user?.role === "admin" || user?.role === "system-admin" ? (
									<div className="pt-2">
										<Button
											variant="destructive"
											onClick={() => setShowCancelDialog(true)}
										>
											{t({
												id: "cancelSubscription",
												message: "Cancel Subscription",
											})}
										</Button>
									</div>
								) : null}

								<AlertDialog
									open={showCancelDialog}
									onOpenChange={setShowCancelDialog}
								>
									<AlertDialogContent>
										<AlertDialogHeader>
											<AlertDialogTitle>
												{t({
													id: "confirmCancelSubTitle",
													message: "Cancel your subscription?",
												})}
											</AlertDialogTitle>
											<AlertDialogDescription>
												{t({
													id: "confirmCancelSubDesc",
													message:
														"Are you sure you want to cancel your subscription? Your plan will remain active until the end of the current billing cycle.",
												})}
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel disabled={isCanceling}>
												{t({
													id: "keepSubscription",
													message: "No, Keep Subscription",
												})}
											</AlertDialogCancel>
											<AlertDialogAction
												disabled={isCanceling}
												onClick={(e) => {
													e.preventDefault();
													handleConfirmCancel();
												}}
												className="bg-danger-tint text-danger-ink hover:bg-danger-tint/80"
											>
												{isCanceling
													? t({ id: "canceling", message: "Canceling..." })
													: t({
															id: "confirmCancel",
															message: "Yes, Cancel Subscription",
														})}
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>

								<div className="border-t border-border pt-4">
									<a
										href="https://provoize.com/tos"
										target="_blank"
										rel="noopener noreferrer"
										className="inline-flex items-center gap-1 text-sm font-semibold text-accent-ink underline-offset-4 hover:underline"
									>
										<span>
											{t({
												id: "termsOfService",
												message: "Terms of Service / תנאי שימוש",
											})}
										</span>
										<ExternalLink className="w-3.5 h-3.5" />
									</a>
								</div>
							</div>
						</CollapsibleSettingsPanel>
					)}
				</SettingsSection>
				<SettingsSection id="call-results">
					<CallResultsSettingsPanel />
				</SettingsSection>
				<SettingsSection id="connections">
					<CollapsibleSettingsPanel
						icon={<MessageCircle className="w-5 h-5" />}
						title={t({ id: "whatsappConnection", message: "WhatsApp" })}
						description={t({
							id: "whatsappConnectionDescription",
							message: "Connect your WhatsApp Business account.",
						})}
						headerAction={
							<Badge
								variant="outline"
								className={
									companyProfile?.whatsappConnection?.status === "connected"
										? "border-live/30 bg-live-tint text-foreground"
										: "border-border bg-secondary text-muted-foreground"
								}
							>
								{companyProfile?.whatsappConnection?.status === "connected"
									? t({
											id: "whatsappConnectionStatus.connected",
											message: "Connected",
										})
									: t({
											id: "whatsappConnectionStatus.notConnected",
											message: "Not connected",
										})}
							</Badge>
						}
					>
						<div className="space-y-2">
							{companyProfile?.whatsappConnection?.credentialMode ===
							"borrowed" ? (
								<div className="space-y-2 text-sm" role="status">
									<p className="font-semibold">
										{t({
											id: "whatsappTemplates.borrowed",
											message: "Borrowed WhatsApp account",
										})}{" "}
										· {companyProfile.whatsappConnection.sourceProject}
									</p>
									<p>
										{companyProfile.whatsappConnection.responsesEnabled &&
										Date.parse(
											companyProfile.whatsappConnection
												.responseLeaseExpiresAt || "",
										) > Date.now()
											? t({
													id: "whatsappTemplates.responsesActive",
													message:
														"External reply relay active for selected test numbers.",
												})
											: t({
													id: "whatsappBorrowedSettingsHelp",
													message:
														"This connection is managed externally. Connect, disconnect and automatic replies are unavailable. Use the removal script when testing is complete.",
												})}
									</p>
									<p>
										{t({ id: "whatsappTemplates.expiry", message: "Expires" })}:{" "}
										{companyProfile.whatsappConnection.expiresAt
											? new Date(
													companyProfile.whatsappConnection.expiresAt,
												).toLocaleString()
											: "—"}
									</p>
								</div>
							) : companyProfile?.whatsappConnection?.status === "connected" ? (
								<div className="flex items-center gap-3">
									<p className="text-sm font-medium text-foreground">
										{t({
											id: "whatsappConnected",
											message: "WhatsApp connected",
										})}
									</p>
									<Button
										variant="outline"
										onClick={() => setShowWhatsAppDisconnectDialog(true)}
									>
										{t({ id: "whatsappDisconnect", message: "Disconnect" })}
									</Button>
								</div>
							) : (
								<div className="space-y-2">
									{!companyProfile?.whatsappConnection && (
										<p className="text-sm font-medium text-muted-foreground">
											{t({
												id: "whatsappNotConnected",
												message: "Not Connected",
											})}
										</p>
									)}
									<div className="flex flex-wrap gap-3">
										<ConnectWhatsAppButton
											appId={import.meta.env.VITE_META_APP_ID}
											configId={import.meta.env.VITE_META_WHATSAPP_CONFIG_ID}
											flow="business_app"
											label={t({
												id: "whatsappConnectExisting",
												message: "Connect existing WhatsApp Business App",
											})}
											className="rounded-md bg-live-tint px-4 py-2 text-sm font-semibold text-foreground hover:bg-live-tint/80 disabled:cursor-not-allowed disabled:opacity-50"
										/>
										<ConnectWhatsAppButton
											appId={import.meta.env.VITE_META_APP_ID}
											configId={import.meta.env.VITE_META_WHATSAPP_CONFIG_ID}
											flow="new_number"
											label={t({
												id: "whatsappConnectNew",
												message: "Connect WhatsApp (new number)",
											})}
											className="rounded-md bg-live-tint px-4 py-2 text-sm font-semibold text-foreground hover:bg-live-tint/80 disabled:cursor-not-allowed disabled:opacity-50"
										/>
									</div>
								</div>
							)}
						</div>
					</CollapsibleSettingsPanel>

					<CollapsibleSettingsPanel
						icon={<PhoneOutgoing className="w-5 h-5" />}
						title={t({
							id: "outboundCallerIdTitle",
							message: "Outbound caller ID",
						})}
						description={t({
							id: "outboundCallerIdDescription",
							message:
								"A verified number is shown to recipients for outbound calls. Unverified numbers are never used.",
						})}
						headerAction={
							configuredOutboundCallerId ? (
								<Badge
									variant="outline"
									className={
										configuredOutboundCallerId.status === "verified"
											? "border-live/30 bg-live-tint text-foreground"
											: "border-warn/30 bg-warn-tint text-foreground"
									}
								>
									{configuredOutboundCallerId.status === "verified"
										? t({ id: "outboundCallerIdVerified", message: "Verified" })
										: t({
												id: "outboundCallerIdUnverified",
												message: "Unverified",
											})}
								</Badge>
							) : (
								<Badge variant="outline">
									{t({ id: "outboundCallerIdNotSet", message: "Not set" })}
								</Badge>
							)
						}
					>
						<div className="space-y-4">
							<div className="space-y-2">
								<Label htmlFor="outboundCallerId">
									{t({
										id: "outboundCallerIdPhoneNumber",
										message: "Phone number",
									})}
								</Label>
								<div className="flex flex-col gap-3 sm:flex-row">
									<Input
										id="outboundCallerId"
										value={outboundCallerIdInput}
										onChange={(event) =>
											setOutboundCallerIdInput(event.target.value)
										}
										placeholder="+972..."
										dir="ltr"
										disabled={!isAdmin || outboundCallerIdAction !== null}
									/>
									{isAdmin && (
										<Button
											onClick={handleStartOutboundCallerIdVerification}
											disabled={
												!outboundCallerIdInput.trim() ||
												outboundCallerIdAction !== null
											}
										>
											{outboundCallerIdAction === "start" && (
												<Loader2 className="w-4 h-4 me-2 animate-spin" />
											)}
											{t({
												id: "outboundCallerIdVerify",
												message: "Verify number",
											})}
										</Button>
									)}
								</div>
							</div>

							{configuredOutboundCallerId && (
								<div className="flex flex-wrap items-center gap-3 rounded-md border p-3">
									{configuredOutboundCallerId.status === "verified" ? (
										<ShieldCheck className="h-5 w-5 text-live" />
									) : (
										<ShieldX className="h-5 w-5 text-warn" />
									)}
									<span dir="ltr" className="font-medium">
										{formatPhoneNumber(configuredOutboundCallerId.phoneNumber)}
									</span>
									<span className="text-sm font-semibold text-foreground">
										{configuredOutboundCallerId.status === "verified"
											? t({
													id: "outboundCallerIdVerified",
													message: "Verified",
												})
											: t({
													id: "outboundCallerIdUnverified",
													message: "Unverified",
												})}
									</span>
									{isAdmin && (
										<div className="ms-auto flex gap-2">
											{configuredOutboundCallerId.status !== "verified" && (
												<Button
													variant="outline"
													onClick={handleRefreshOutboundCallerId}
													disabled={outboundCallerIdAction !== null}
												>
													<RefreshCw
														className={`w-4 h-4 me-2 ${outboundCallerIdAction === "refresh" ? "animate-spin" : ""}`}
													/>
													{t({
														id: "outboundCallerIdCheckStatus",
														message: "Check status",
													})}
												</Button>
											)}
											<Button
												variant="outline"
												onClick={handleRemoveOutboundCallerId}
												disabled={outboundCallerIdAction !== null}
											>
												{t({
													id: "outboundCallerIdRemove",
													message: "Remove",
												})}
											</Button>
										</div>
									)}
								</div>
							)}

							{outboundCallerIdValidationCode && (
								<div className="rounded-md border border-ring/30 bg-accent p-4 text-foreground">
									<p className="text-sm">
										{t({
											id: "outboundCallerIdCodeInstructions",
											message:
												"Answer Twilio's verification call and enter this code:",
										})}
									</p>
									<p
										className="mt-2 font-mono text-2xl font-bold tracking-widest"
										dir="ltr"
									>
										{outboundCallerIdValidationCode}
									</p>
								</div>
							)}
						</div>
					</CollapsibleSettingsPanel>

					<AlertDialog
						open={showWhatsAppDisconnectDialog}
						onOpenChange={setShowWhatsAppDisconnectDialog}
					>
						<AlertDialogContent>
							<AlertDialogHeader>
								<AlertDialogTitle>
									{t({
										id: "whatsappDisconnectTitle",
										message: "Disconnect WhatsApp?",
									})}
								</AlertDialogTitle>
								<AlertDialogDescription>
									{t({
										id: "whatsappDisconnectDescription",
										message:
											"Provoize will stop receiving messages. Your WhatsApp Business Account will not be deleted.",
									})}
								</AlertDialogDescription>
							</AlertDialogHeader>
							<AlertDialogFooter>
								<AlertDialogCancel disabled={isDisconnectingWhatsApp}>
									{t({ id: "cancel", message: "Cancel" })}
								</AlertDialogCancel>
								<AlertDialogAction
									onClick={(event) => {
										event.preventDefault();
										void handleDisconnectWhatsApp();
									}}
									disabled={isDisconnectingWhatsApp}
								>
									{isDisconnectingWhatsApp
										? t({ id: "disconnecting", message: "Disconnecting..." })
										: t({ id: "whatsappDisconnect", message: "Disconnect" })}
								</AlertDialogAction>
							</AlertDialogFooter>
						</AlertDialogContent>
					</AlertDialog>
				</SettingsSection>
				<SettingsSection id="tools">
					{isAdmin && companyProfile?.id && (
						<>
							<GoogleCalendarSettingsCard
								key={`calendar-${companyProfile.id}`}
								companyId={companyProfile.id}
								agents={agents}
							/>
							<GrokbotSettingsCard
								key={`grokbot-${companyProfile.id}`}
								companyId={companyProfile.id}
								agents={agents}
							/>
							<SancarToolsPanel
								key={`sancar-${companyProfile.id}`}
								companyId={companyProfile.id}
							/>
							<DtmfSettingsCard key={`dtmf-${companyProfile.id}`} />
						</>
					)}
				</SettingsSection>
				<SettingsSection id="users">
					<CollapsibleSettingsPanel
						icon={<Users className="w-5 h-5" />}
						title={t({ id: "users", message: "Users" })}
						description={t({
							id: "usersSubtitle",
							message: "View and manage company users",
						})}
						headerAction={<Badge variant="secondary">{users.length}</Badge>}
					>
						<div className="space-y-4">
							{isAdmin ? (
								<div className="flex justify-end">
									<Button onClick={() => setIsInviteDialogOpen(true)}>
										<UserPlus className="w-4 h-4 me-2" />
										{t({ id: "inviteUser", message: "Invite user" })}
									</Button>
								</div>
							) : null}
							<div className="overflow-x-auto">
								<Table className="min-w-[760px]">
									<TableHeader>
										<TableRow>
											<TableHead
												className={
													language === "he" ? "text-right" : "text-left"
												}
											>
												{t({ id: "fullName", message: "Full Name" })}
											</TableHead>
											<TableHead
												className={
													language === "he" ? "text-right" : "text-left"
												}
											>
												{t({ id: "emailAddress", message: "Email Address" })}
											</TableHead>
											<TableHead
												className={
													language === "he" ? "text-right" : "text-left"
												}
											>
												{t({ id: "phoneNumber", message: "Phone Number" })}
											</TableHead>
											<TableHead
												className={
													language === "he" ? "text-right" : "text-left"
												}
											>
												{t({ id: "role", message: "Role" })}
											</TableHead>
											<TableHead
												className={
													language === "he" ? "text-right" : "text-left"
												}
											>
												{t({ id: "internalAuth", message: "Internal auth" })}
											</TableHead>
											<TableHead></TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{users.map((u) => (
											<TableRow key={u.uid}>
												<TableCell>
													{u.displayName || t({ id: "noValue", message: "—" })}
												</TableCell>
												<TableCell>
													<Tooltip delayDuration={3000}>
														<TooltipTrigger className="cursor-help">
															{u.email || t({ id: "noValue", message: "—" })}
														</TooltipTrigger>
														<TooltipContent>
															<p>
																{t({ id: "userId", message: "User ID" })}:{" "}
																{u.uid}
															</p>
														</TooltipContent>
													</Tooltip>
												</TableCell>
												<TableCell dir="ltr">
													{u.phoneNumber
														? formatPhoneNumber(u.phoneNumber)
														: t({ id: "noValue", message: "—" })}
												</TableCell>
												<TableCell
													className={`capitalize ${language === "he" ? "text-right" : "text-left"}`}
												>
													{userRoleLabel(u.role)}
												</TableCell>
												<TableCell>
													{u.internalAuth?.configured
														? t({ id: "configured", message: "Configured" })
														: t({
																id: "notConfigured",
																message: "Not configured",
															})}
												</TableCell>
												<TableCell className="w-12 text-end">
													{(isAdmin || user?.uid === (u.uid || u.id)!) && (
														<DropdownMenu>
															<DropdownMenuTrigger asChild>
																<Button
																	variant="ghost"
																	size="icon"
																	aria-label={t({
																		id: "userActions",
																		message: "User actions",
																	})}
																>
																	<MoreHorizontal className="h-4 w-4" />
																</Button>
															</DropdownMenuTrigger>
															<DropdownMenuContent align="end">
																<DropdownMenuItem
																	onClick={() => setPhraseResetUser(u)}
																>
																	<Fingerprint className="h-4 w-4" />
																	{t({
																		id: "resetInternalAuthPhrase",
																		message: "Reset internal auth phrase",
																	})}
																</DropdownMenuItem>
																{isAdmin && (
																	<>
																		<DropdownMenuItem
																			onClick={() => setPasswordResetUser(u)}
																		>
																			<KeyRound className="h-4 w-4" />
																			{t({
																				id: "sendPasswordReset",
																				message: "Send password reset",
																			})}
																		</DropdownMenuItem>
																		<DropdownMenuItem
																			onClick={() => handleEditUser(u)}
																		>
																			<Edit className="h-4 w-4" />
																			{t({
																				id: "editUser",
																				message: "Edit User",
																			})}
																		</DropdownMenuItem>
																		<DropdownMenuSeparator />
																		<DropdownMenuItem
																			className="text-danger-ink focus:text-danger-ink"
																			onClick={() => setUserToDelete(u)}
																		>
																			<Trash2 className="h-4 w-4" />
																			{t({
																				id: "deleteUser",
																				message: "Delete user",
																			})}
																		</DropdownMenuItem>
																	</>
																)}
															</DropdownMenuContent>
														</DropdownMenu>
													)}
												</TableCell>
											</TableRow>
										))}
										{users.length === 0 && (
											<TableRow>
												<TableCell
													colSpan={6}
													className="text-center py-4 text-muted-foreground"
												>
													{t({ id: "noUsersFound", message: "No users found" })}
												</TableCell>
											</TableRow>
										)}
									</TableBody>
								</Table>
							</div>

							<Dialog
								open={isInviteDialogOpen}
								onOpenChange={(open) => {
									if (!isInvitingUser) setIsInviteDialogOpen(open);
								}}
							>
								<DialogContent>
									<DialogHeader>
										<DialogTitle>
											{t({ id: "inviteUser", message: "Invite user" })}
										</DialogTitle>
										<DialogDescription>
											{t({
												id: "inviteUserDescription",
												message:
													"Invite a user to this company, choose their role, and optionally add their phone number.",
											})}
										</DialogDescription>
									</DialogHeader>
									<div className="space-y-4 py-4">
										<div className="space-y-2">
											<Label htmlFor="invite-user-name">
												{t({ id: "fullName", message: "Full Name" })}
											</Label>
											<Input
												id="invite-user-name"
												value={inviteName}
												onChange={(event) => setInviteName(event.target.value)}
												disabled={isInvitingUser}
												{...contentFieldProps(inviteName)}
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="invite-user-email">
												{t({ id: "emailAddress", message: "Email Address" })}
											</Label>
											<Input
												id="invite-user-email"
												type="email"
												dir="ltr"
												className="text-left"
												value={inviteEmail}
												onChange={(event) => setInviteEmail(event.target.value)}
												disabled={isInvitingUser}
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="invite-user-phone">
												{t({
													id: "phoneNumberOptional",
													message: "Phone Number (optional)",
												})}
											</Label>
											<Input
												id="invite-user-phone"
												type="tel"
												value={invitePhone}
												onChange={(event) => setInvitePhone(event.target.value)}
												disabled={isInvitingUser}
												dir="ltr"
												placeholder="+14155552671"
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="invite-user-role">
												{t({ id: "role", message: "Role" })}
											</Label>
											<Select
												value={inviteRole}
												onValueChange={(value: "user" | "admin") =>
													setInviteRole(value)
												}
												disabled={isInvitingUser}
											>
												<SelectTrigger id="invite-user-role">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="user">
														{t({ id: "userRole", message: "User" })}
													</SelectItem>
													<SelectItem value="admin">
														{t({ id: "adminRole", message: "Admin" })}
													</SelectItem>
												</SelectContent>
											</Select>
										</div>
									</div>
									<DialogFooter>
										<Button
											variant="outline"
											onClick={() => setIsInviteDialogOpen(false)}
											disabled={isInvitingUser}
										>
											{t({ id: "cancel", message: "Cancel" })}
										</Button>
										<Button
											onClick={handleInviteUser}
											disabled={isInvitingUser}
										>
											{isInvitingUser && (
												<Loader2 className="w-4 h-4 me-2 animate-spin" />
											)}
											{isInvitingUser
												? t({ id: "sendingInvitation", message: "Sending..." })
												: t({
														id: "sendInvitation",
														message: "Send invitation",
													})}
										</Button>
									</DialogFooter>
								</DialogContent>
							</Dialog>

							<Dialog
								open={!!phraseResetUser}
								onOpenChange={(open) => {
									if (!open && !isSavingAuthPhrase) {
										setPhraseResetUser(null);
										setAuthPhrase("");
										setAuthPhraseConfirm("");
									}
								}}
							>
								<DialogContent>
									<DialogHeader>
										<DialogTitle>
											{t({
												id: "resetInternalAuthPhrase",
												message: "Reset internal auth phrase",
											})}
										</DialogTitle>
										<DialogDescription>
											{t({
												id: "internalAuthPhraseDescription",
												message:
													"This phrase is used when this company user calls one of your agents and needs to enter Company Internal Mode. They will say it during the call after the agent asks for it.",
											})}
										</DialogDescription>
									</DialogHeader>
									<div className="space-y-4 py-4">
										<div className="space-y-2">
											<Label htmlFor="internal-auth-phrase">
												{t({
													id: "internalAuthPhrase",
													message: "Authentication phrase",
												})}
											</Label>
											<Input
												id="internal-auth-phrase"
												type="text"
												value={authPhrase}
												onChange={(event) => setAuthPhrase(event.target.value)}
												disabled={isSavingAuthPhrase}
												{...contentFieldProps(authPhrase)}
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="internal-auth-phrase-confirm">
												{t({
													id: "confirmInternalAuthPhrase",
													message: "Confirm authentication phrase",
												})}
											</Label>
											<Input
												id="internal-auth-phrase-confirm"
												type="text"
												value={authPhraseConfirm}
												onChange={(event) =>
													setAuthPhraseConfirm(event.target.value)
												}
												disabled={isSavingAuthPhrase}
												{...contentFieldProps(authPhraseConfirm)}
											/>
										</div>
									</div>
									<DialogFooter>
										<Button
											variant="outline"
											onClick={() => {
												setPhraseResetUser(null);
												setAuthPhrase("");
												setAuthPhraseConfirm("");
											}}
											disabled={isSavingAuthPhrase}
										>
											{t({ id: "cancel", message: "Cancel" })}
										</Button>
										<Button
											onClick={handleSaveAuthPhrase}
											disabled={isSavingAuthPhrase}
										>
											{isSavingAuthPhrase && (
												<Loader2 className="w-4 h-4 me-2 animate-spin" />
											)}
											{isSavingAuthPhrase
												? t({ id: "saving", message: "Saving..." })
												: t({ id: "savePhrase", message: "Save phrase" })}
										</Button>
									</DialogFooter>
								</DialogContent>
							</Dialog>

							<Dialog
								open={!!editingUser}
								onOpenChange={(open) => !open && setEditingUser(null)}
							>
								<DialogContent>
									<DialogHeader>
										<DialogTitle>
											{t({ id: "editUser", message: "Edit User" })}
										</DialogTitle>
										<DialogDescription className="sr-only">
											{t({
												id: "editUserDescription",
												message: "Update user profile details",
											})}
										</DialogDescription>
									</DialogHeader>
									<div className="space-y-4 py-4">
										<div className="space-y-2">
											<Label htmlFor="edit-user-id">
												{t({ id: "userId", message: "User ID" })}
											</Label>
											<Input
												id="edit-user-id"
												dir="ltr"
												value={editingUser?.uid || editingUser?.id || ""}
												disabled
												className="bg-secondary text-left"
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="edit-user-email">
												{t({ id: "emailAddress", message: "Email Address" })}
											</Label>
											<Input
												id="edit-user-email"
												dir="ltr"
												value={editingUser?.email || ""}
												disabled
												className="bg-secondary text-left"
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="edit-user-name">
												{t({ id: "fullName", message: "Full Name" })}
											</Label>
											<Input
												id="edit-user-name"
												value={editUserName}
												onChange={(e) => setEditUserName(e.target.value)}
												{...contentFieldProps(editUserName)}
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="edit-user-phone">
												{t({ id: "phoneNumber", message: "Phone Number" })}
											</Label>
											<Input
												id="edit-user-phone"
												type="tel"
												value={editUserPhone}
												onChange={(e) => setEditUserPhone(e.target.value)}
												dir="ltr"
												placeholder="+14155552671"
											/>
										</div>
										<div className="space-y-2">
											<Label htmlFor="edit-user-role">
												{t({ id: "role", message: "Role" })}
											</Label>
											<Select
												value={editUserRole}
												onValueChange={(value: "user" | "admin") =>
													setEditUserRole(value)
												}
											>
												<SelectTrigger id="edit-user-role">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="user">
														{t({ id: "userRole", message: "User" })}
													</SelectItem>
													<SelectItem value="admin">
														{t({ id: "adminRole", message: "Admin" })}
													</SelectItem>
												</SelectContent>
											</Select>
										</div>
										{(editingUser?.uid === user?.uid ||
											editingUser?.id === user?.uid) && (
											<div className="space-y-2 pt-2 border-t">
												<Label>
													{t({
														id: "googleAuthentication",
														message: "Google Authentication",
													})}
												</Label>
												{isGoogleLinked(auth?.currentUser || null) ? (
													<div className="flex items-center gap-2 text-sm text-foreground bg-live-tint p-2.5 rounded-md border border-live/30">
														<CheckCircle2 className="w-4 h-4 shrink-0 text-foreground" />
														<span>
															{t({
																id: "googleAccountLinkedIndication",
																message: "Account is linked to Google provider",
															})}
														</span>
													</div>
												) : (
													<Button
														type="button"
														variant="outline"
														className="w-full flex items-center justify-center gap-2"
														onClick={handleLinkGoogle}
														disabled={isLinkingGoogle}
													>
														{isLinkingGoogle ? (
															<Loader2 className="w-4 h-4 animate-spin" />
														) : (
															<svg className="w-4 h-4" viewBox="0 0 24 24">
																<path
																	fill="#4285F4"
																	d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
																/>
																<path
																	fill="#34A853"
																	d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
																/>
																<path
																	fill="#FBBC05"
																	d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
																/>
																<path
																	fill="#EA4335"
																	d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
																/>
															</svg>
														)}
														{t({
															id: "linkWithGoogle",
															message: "Link with Google Provider",
														})}
													</Button>
												)}
											</div>
										)}
									</div>
									<DialogFooter>
										<Button
											variant="outline"
											onClick={() => setEditingUser(null)}
										>
											{t({ id: "cancel", message: "Cancel" })}
										</Button>
										<Button onClick={handleSaveUser} disabled={isSavingUser}>
											{isSavingUser
												? t({ id: "saving", message: "Saving..." })
												: t({ id: "saveChanges", message: "Save Changes" })}
										</Button>
									</DialogFooter>
								</DialogContent>
							</Dialog>

							<AlertDialog
								open={!!userToDelete}
								onOpenChange={(open) => !open && setUserToDelete(null)}
							>
								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>
											{t({
												id: "confirmDeleteUserTitle",
												message: "Are you sure?",
											})}
										</AlertDialogTitle>
										<AlertDialogDescription>
											{i18n._({
												id: "confirmDeleteUserDesc",
												message:
													"This action will remove the user {name} from the company and cannot be undone.",
												values: {
													name:
														userToDelete?.displayName ||
														userToDelete?.email ||
														userToDelete?.uid ||
														"",
												},
											})}
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel disabled={isDeletingUser}>
											{t({ id: "cancel", message: "Cancel" })}
										</AlertDialogCancel>
										<AlertDialogAction
											className="bg-danger-tint text-danger-ink hover:bg-danger-tint/80"
											disabled={isDeletingUser}
											onClick={(e) => {
												e.preventDefault();
												handleDeleteUser();
											}}
										>
											{isDeletingUser && (
												<Loader2
													className="w-4 h-4 me-2 animate-spin"
													aria-hidden="true"
												/>
											)}
											{isDeletingUser
												? t({ id: "deleting", message: "Deleting..." })
												: t({ id: "delete", message: "Delete" })}
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>

							<AlertDialog
								open={!!passwordResetUser}
								onOpenChange={(open) => {
									if (!open && !isSendingPasswordReset)
										setPasswordResetUser(null);
								}}
							>
								<AlertDialogContent>
									<AlertDialogHeader>
										<AlertDialogTitle>
											{t({
												id: "sendPasswordResetTitle",
												message: "Send password reset?",
											})}
										</AlertDialogTitle>
										<AlertDialogDescription>
											{i18n._({
												id: "sendPasswordResetDescription",
												message:
													"Send a password reset email to {email}? The link will be sent directly to the user.",
												values: { email: passwordResetUser?.email || "" },
											})}
										</AlertDialogDescription>
									</AlertDialogHeader>
									<AlertDialogFooter>
										<AlertDialogCancel disabled={isSendingPasswordReset}>
											{t({ id: "cancel", message: "Cancel" })}
										</AlertDialogCancel>
										<AlertDialogAction
											disabled={isSendingPasswordReset}
											onClick={(event) => {
												event.preventDefault();
												void handleSendPasswordReset();
											}}
										>
											{isSendingPasswordReset && (
												<Loader2 className="w-4 h-4 me-2 animate-spin" />
											)}
											{isSendingPasswordReset
												? t({ id: "sending", message: "Sending..." })
												: t({ id: "send", message: "Send" })}
										</AlertDialogAction>
									</AlertDialogFooter>
								</AlertDialogContent>
							</AlertDialog>
						</div>
					</CollapsibleSettingsPanel>
				</SettingsSection>
				<SettingsSection id="preferences">
					{/* Language Settings */}
					<CollapsibleSettingsPanel
						icon={<Languages className="w-5 h-5" />}
						title={t({ id: "language", message: "Language" })}
						description={t({
							id: "selectLanguage",
							message: "Select your preferred language",
						})}
						headerAction={
							<Badge variant="secondary">
								{language === "he"
									? t({ id: "hebrew", message: "Hebrew (עברית)" })
									: t({ id: "english", message: "English" })}
							</Badge>
						}
					>
						<div className="contents">
							<div className="space-y-2">
								<Label htmlFor="language">
									{t({ id: "language", message: "Language" })}
								</Label>
								<Select
									value={language}
									onValueChange={(value: "en" | "he") => setLanguage(value)}
								>
									<SelectTrigger id="language" className="w-full md:w-[300px]">
										<SelectValue />
									</SelectTrigger>
									<SelectContent>
										<SelectItem value="en">
											{t({ id: "english", message: "English" })}
										</SelectItem>
										<SelectItem value="he">
											{t({ id: "hebrew", message: "Hebrew (עברית)" })}
										</SelectItem>
									</SelectContent>
								</Select>
							</div>
						</div>
					</CollapsibleSettingsPanel>
				</SettingsSection>
				<SettingsSection id="developer">
					{isAdmin &&
						companyProfile?.id &&
						companyProfile.features?.apiAccess === true && (
							<>
								<DeveloperKeysSettingsCard
									companyId={companyProfile.id}
									language={language as "en" | "he"}
								/>
								<ExternalAgentDeploymentApprovalsCard
									companyId={companyProfile.id}
								/>
								<ExternalOperationApprovalsCard companyId={companyProfile.id} />
							</>
						)}
				</SettingsSection>
			</SettingsNavigation>
		</div>
	);
}
